import React from "react";
import { Link } from "react-router-dom"; 
import { useTranslation } from "react-i18next";

/** Minimal shape you already use in several places */
export type MediaEntry = {
  url: string;
  musicTitle?: string | null;
  musicArtist?: string | null;
};

/** What the overlay actually consumes */
export type ViewerItem = {
  url: string;
  label?: string; // "Song — Artist"
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};

/** Build the overlay items from your mixed arrays (string | MediaEntry | any) */
export function viewerItemsFromMixed(
  media: Array<string | MediaEntry | any>,
  toAbsUrl: (p: any) => string
): ViewerItem[] {
  const labelOf = (e: any): string => {
    const t =
      (e?.musicTitle ??
        e?.songTitle ??
        e?.trackTitle ??
        e?.music?.title ??
        e?.audio?.title ??
        "")?.toString()
        .trim() || "";
    const a =
      (e?.musicArtist ??
        e?.songArtist ??
        e?.trackArtist ??
        e?.music?.artist ??
        e?.audio?.artist ??
        "")?.toString()
        .trim() || "";
    if (!t && !a) return "";
    return t && a ? `${t} — ${a}` : (t || a);
  };

  // Replace your extractUrl with this version
const extractUrl = (m: any): string => {
  if (!m) return "";

  // Caller can pass a direct string
  if (typeof m === "string") return toAbsUrl(m) || "";

  // Common fields you already support
  const raw =
    m.url ??
    m.videoUrl ??
    m.src ??
    m.path ??
    m.file ?? // ← if this happens to be a File, guard below
    m?.media?.url ??
    m?.video?.url ??
    m?.image?.url ??
    m?.url?.secure_url ??
    m?.url?.url ??
    "";

  // Always turn what we got into a string
  let u = toAbsUrl(raw);
  if (typeof u !== "string") u = String(u || "");

  // If someone accidentally passed a File/Blob down here,
  // DO NOT rely on implicit coercion. Just bail (overlay expects urls).
  if (!/^blob:|^https?:|^data:/i.test(u)) return "";

  // If it’s a blob: without an extension and we know it’s a video by MIME,
  // add a hash hint so video detectors (and some players) accept it.
  if (
    u.startsWith("blob:") &&
    !/\.(mp4|webm|ogg|mov|m4v)(?=($|[?#]))/i.test(u)
  ) {
    const mime =
      m?.mime || m?.mediaMime || m?.type || m?.file?.type || "";
    if (/^video\//i.test(mime)) {
      u += (u.includes("#") ? "" : "#") + "ext=.mp4";
    }
  }

  return u;
};


  return (Array.isArray(media) ? media : [])
    .map((e) => {
      const url = extractUrl(e);
      if (!url) return null;
      return {
        url,
        label: labelOf(e),
        musicId:
          e?.musicId ?? e?.music?._id ?? e?.audio?._id ??
          e?.trackId ?? null,
        musicTitle:
          e?.musicTitle ?? e?.songTitle ?? e?.trackTitle ??
          e?.music?.title ?? e?.audio?.title ?? null,
        musicArtist:
          e?.musicArtist ?? e?.songArtist ?? e?.trackArtist ??
          e?.music?.artist ?? e?.audio?.artist ?? null,
      } as ViewerItem;
    })
    .filter(Boolean) as ViewerItem[];
}

/** One-liner predicate */
const isVideoUrl = (u?: string) => {
  if (!u) return false;

  // 1) obvious: data:video/...
  if (u.startsWith('data:video/')) return true;

  // 2) real extension before ? or # (matches ...mp4, ...mp4?x, ...mp4#y)
  if (/\.(mp4|webm|ogg|mov|m4v)(?=($|[?#]))/i.test(u)) return true;

  // 3) hints in query OR hash: ?ext=.mp4, #ext=.mp4, ?filename=foo.mp4, #filename=foo.mp4
  const tail = u.split('?')[1] || '';
  const hash = u.split('#')[1] || '';
  const hint = `${tail}&${hash}`;
  if (/(^|[&#?])(ext|filename)=\.?(mp4|webm|ogg|mov|m4v)\b/i.test(hint)) return true;

  return false;
};


/** Inline styles so you don’t need to bring CSS along */
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.97)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  overflow: "hidden",
};

const contentWrapStyle: React.CSSProperties = {
  position: "relative",
  width: "100vw",
  height: "100vh",
  display: "grid",
  placeItems: "center",
};

const baseMediaStyle: React.CSSProperties = {
  width: "auto",
  height: "auto",
  objectFit: "contain",
  borderRadius: 12,
  boxShadow: "0 0 20px rgba(0,0,0,0.5)",
};


// small helper: returns a tuned style for landscape vs portrait
function sizedStyleFor(
  aspect?: { w: number; h: number } | null
): React.CSSProperties {
  if (!aspect) {
    return {
      ...baseMediaStyle,
      maxWidth: "99vw",
      maxHeight: "94vh",
    };
  }

  const landscape = aspect.w >= aspect.h;

  if (landscape) {
    return {
      ...baseMediaStyle,
      maxWidth: "94vw",  // was 88vw
      maxHeight: "78vh", // was 72vh
    };
  }

  return {
    ...baseMediaStyle,
    maxWidth: "78vw",   // was 72vw
    maxHeight: "92vh",  // was 88vh
  };
}


const navBtn: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(0,0,0,0.55)",
  color: "#ccc",
  border: "none",
  fontSize: 28,
  padding: "10px 14px",
  cursor: "pointer",
  borderRadius: 10,
  zIndex: 10,
  lineHeight: 1,
};

const closeBtn: React.CSSProperties = {
  position: "absolute",
  top: 14,
  right: 21,
  background: "rgba(35, 12, 12, 0.55)",
  color: "#ccc",
  border: "none",
  fontSize: 16,
  padding: "10px 12px",
  cursor: "pointer",
  borderRadius: 9999,
  zIndex: 10,
  lineHeight: 1,
};

const labelBar: React.CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 22,
  padding: "8px 12px",
  fontSize: 14,
  color: "#555",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  background:
    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.65) 100%)",
};

/** Props for controlled overlay */
export type MediaViewerOverlayProps = {
  items: ViewerItem[];
  index: number;
  isOpen: boolean;
  onClose: () => void;
  onIndexChange: (i: number) => void;
  muted?: boolean;
  onMutedChange?: (m: boolean) => void;
  pauseBackgroundOnOpen?: boolean;

  /** Called when a label without musicId is clicked */
  onOpenMusic?: (info: {
    musicId?: string | null;
    musicTitle?: string | null;
    musicArtist?: string | null;
    label?: string;
  }) => void;
};

/**
 * Controlled overlay component.
 * - Shows 1 image/video at a time with prev/next arrows
 * - Displays music label if present
 * - Locks body scroll while open
 * - Ensures ONLY ONE <video> plays at a time (singleton listener)
 */
export const MediaViewerOverlay: React.FC<MediaViewerOverlayProps> = ({
  items,
  index,
  isOpen,
  onClose,
  onIndexChange,
  muted = true,
  onMutedChange,
  pauseBackgroundOnOpen = true,
  onOpenMusic,
}) => {
  const { t } = useTranslation(); 
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const cur = items[index];
  const isVid = isVideoUrl(cur?.url);
// inside MediaViewerOverlay component
const [uiActive, setUiActive] = React.useState(false);
const idleTimer = React.useRef<number | null>(null);
const [aspect, setAspect] = React.useState<{ w: number; h: number } | null>(null);

const bumpUI = React.useCallback((lingerMs = 300) => {
  setUiActive(true);
  if (idleTimer.current) window.clearTimeout(idleTimer.current);
  idleTimer.current = window.setTimeout(() => {
    setUiActive(false);
    idleTimer.current = null;
  }, lingerMs);
}, []);

React.useEffect(() => {
  return () => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
  };
}, []);

  // Lock body scroll while the viewer is open
  React.useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const { overflow, position, top, width } = document.body.style;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.width = width;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Keyboard nav
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        onIndexChange(Math.min(items.length - 1, index + 1));
      if (e.key === "ArrowLeft") onIndexChange(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, index, items.length, onClose, onIndexChange]);

  // Pause any other playing <video> in the document (singleton)
  React.useEffect(() => {
    const handlePlay = (e: Event) => {
      const target = e.target as HTMLVideoElement;
      if (!target || target.tagName !== "VIDEO") return;
      document.querySelectorAll("video").forEach((v) => {
        if (v !== target) {
          try {
            v.pause();
          } catch {}
        }
      });
    };
    document.addEventListener("play", handlePlay, true);
    return () => document.removeEventListener("play", handlePlay, true);
  }, []);

  // Pause background feed videos on open (optional)
  React.useEffect(() => {
    if (!isOpen || !pauseBackgroundOnOpen) return;
    try {
      document.querySelectorAll("video").forEach((v) => {
        if (!v.paused && !v.ended) v.pause();
      });
    } catch {}
  }, [isOpen, pauseBackgroundOnOpen]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true">
      <div
  style={contentWrapStyle}
  onClick={(e) => e.stopPropagation()}
  onMouseEnter={() => setUiActive(true)}
  onMouseLeave={() => setUiActive(false)}
  onMouseMove={() => bumpUI(1200)}
  onFocus={() => setUiActive(true)}
  onBlur={() => setUiActive(false)}
  onTouchStart={() => bumpUI(1500)}
>
        {/* Close */}
        <button style={closeBtn} onClick={onClose} aria-label={t('mediaViewer.closeLabel')}>
          ✖
        </button>

        {/* Prev */}
        <button
          style={{ ...navBtn, left: 20, visibility: index === 0 ? "hidden" : "visible" }}
          onClick={() => onIndexChange(Math.max(0, index - 1))}
          aria-label={t('mediaViewer.prevLabel')}
        >
          ‹
        </button>

        {/* Media */}
{isVid ? (
  <video
    ref={videoRef}
    src={cur?.url}
    style={sizedStyleFor(aspect)}
    autoPlay
    controls
    playsInline
    muted={muted}
    onLoadedMetadata={(e) => {
      const v = e.currentTarget;
      // videoWidth/videoHeight are reliable for aspect
      setAspect({ w: v.videoWidth || 16, h: v.videoHeight || 9 });
    }}
    onVolumeChange={(e) => onMutedChange?.((e.target as HTMLVideoElement).muted)}
  />
) : (
  <img
    src={cur?.url}
    alt="viewer"
    style={sizedStyleFor(aspect)}
    onLoad={(e) => {
      const i = e.currentTarget;
      setAspect({ w: i.naturalWidth || 3, h: i.naturalHeight || 4 });
    }}
  />
)}

        {/* Music label */}
  {isVid && cur?.label ? (
  <div
    style={{
      ...labelBar,
      opacity: uiActive ? 1 : 0,
      transition: 'opacity 220ms ease',
    }}
    title={cur.label}
    onClick={(e) => e.stopPropagation()} 

  >
    <span aria-hidden="true">🎵</span>
    {cur.musicId ? (
      <Link
        to={`/musicdetails/${cur.musicId}`}
        style={{
          color: '#6e7278',
          fontWeight: 600,
          textDecoration: 'none',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        onClick={(e) => e.stopPropagation()}
        title={`Open details for ${cur.label || 'track'}`}
      >
        {cur.label}
      </Link>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenMusic?.({
            musicId: null,
            musicTitle: cur.musicTitle ?? null,
            musicArtist: cur.musicArtist ?? null,
            label: cur.label,
          });
        }}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: onOpenMusic ? 'pointer' : 'default',
          color: '#6e7278',
          fontWeight: 600,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={onOpenMusic ? `Open details for ${cur.label || 'track'}` : undefined}
      >
        {cur.label}
        </button>
    )}
  </div>
) : null}


        {/* Next */}
        <button
          style={{
            ...navBtn,
            right: 20,
            visibility: index === items.length - 1 ? "hidden" : "visible",
          }}
          onClick={() => onIndexChange(Math.min(items.length - 1, index + 1))}
          aria-label={t('mediaViewer.nextLabel')}
        >
          ›
        </button>
      </div>
    </div>
  );
};

/**
 * Convenience hook so each screen can just:
 * const { openViewer, Overlay } = useMediaViewerOverlay();
 * ...
 * onClick={() => openViewer(viewerItemsFromMixed(post.media, toAbsUrl), i)}
 * ...
 * {Overlay}
 */
export function useMediaViewerOverlay(opts?: {
  initialMuted?: boolean;
  pauseBackgroundOnOpen?: boolean;
  onOpenMusic?: MediaViewerOverlayProps['onOpenMusic'];
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [items, setItems] = React.useState<ViewerItem[]>([]);
  const [muted, setMuted] = React.useState<boolean>(opts?.initialMuted ?? true);

  const open = (viewerItems: ViewerItem[], start = 0) => {
    setItems(viewerItems);
    setIndex(Math.max(0, Math.min(start, viewerItems.length - 1)));
    setIsOpen(true);
  };

  const Overlay = (
    <MediaViewerOverlay
      items={items}
      index={index}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onIndexChange={setIndex}
      muted={muted}
      onMutedChange={setMuted}
      pauseBackgroundOnOpen={opts?.pauseBackgroundOnOpen ?? true}
      onOpenMusic={opts?.onOpenMusic} 
    />
  );

  return {
    openViewer: open,
    closeViewer: () => setIsOpen(false),
    setViewerIndex: setIndex,
    isViewerOpen: isOpen,
    viewerIndex: index,
    viewerItems: items,
    muted,
    setMuted,
    Overlay,
  };
}
